import { useEffect, useState, memo, lazy, Suspense, useDeferredValue, useMemo, useRef } from "react";
import Events from "./Events";
import "./css/Widget.css";
import SelectComponent from "ui-kit/src/SelectComponent/SelectComponent";
import {
	EVENT_COLOR_DEFINITION,
	MapType,
	VISUAL_MAP_OPTIONS,
	EventType,
	EventDistributionEnum,
	InsightCategoryEnum,
	PASS_BODY_PART_FILTER_KEY,
	SHOT_BODY_PART_FILTER_KEY,
	SAVE_BODY_PART_FILTER_KEY,
} from "./constants";
import { _, _any } from "i18n";
import { Loader2 } from "lucide-react";

// Lazy load heavy visual components (PointMap initially, switches to Konva on zoom)
const PointMapWithZoomSwitch = lazy(() => import("./Visual/PointMapWithZoomSwitch"));
const ZonalMap = lazy(() => import("./Visual/ZonalMap"));
const DistributionChart = lazy(() => import("./Visual/DistributionChart"));
const CumulativeProgressionCharts = lazy(() => import("./Visual/CumulativeProgressionCharts"));

import { EventFilterCondition, GPEventResponse, GPGamePlayerResponse } from "common/src/gamePage/types";
import { EVENT_PARAMETER_OPTIONS, SelectOption } from "./ParameterOptions";
import Button from "ui-kit/src/ButtonComponent/Button";
import { useGameStore } from "../../store";
import { TooltipComponent } from "ui-kit/src/TooltipComponent/TooltipComponent";
import Icon from "ui-kit/src/Icon/Icon";
import clsx from "clsx";

interface WidgetProps {
	eventType: EventType;
	parameters: Record<string, string>[] | undefined;
	filters: EventFilterCondition[];
	events: GPEventResponse[];
	players: Record<number, GPGamePlayerResponse>;
	showPlayerNumber?: boolean;
	mapType?: MapType;
	customWidget?: boolean;
	onDeleteCustom?: (identifier: string | number) => void;
	onMapTypeChange?: (identifier: string | number, mapType: MapType) => void;
	isLimitedVisualization?: boolean;
	identifier: string;
	category: InsightCategoryEnum | undefined;
}

const Widget = ({
	eventType,
	parameters,
	filters,
	events,
	players,
	showPlayerNumber = false,
	mapType,
	customWidget = false,
	onDeleteCustom,
	onMapTypeChange,
	isLimitedVisualization = false,
	identifier,
	category,
}: WidgetProps) => {
	const { eventFilter } = useGameStore();
	const [isOpen, setIsOpen] = useState(events.length > 0);
	const [parameterFilters, setParameterFilters] = useState<Record<string, SelectOption>>({});
	const [selectedMapOption, setSelectedMapOption] = useState<SelectOption>(() => {
		const initialOptions = VISUAL_MAP_OPTIONS.map((option) => ({
			...option,
			label: _any(option.label),
		}));
		let available = initialOptions.filter((option) => {
			if (option.condition) return option.condition(eventType as any);
			return true;
		});
		if (isLimitedVisualization) {
			available = available.filter((option) => ![MapType.PointMap, MapType.Directional, MapType.Zonal].includes(option.value as MapType));
		}
		const fromProp = mapType ? available.find((o) => o.value === mapType) : undefined;
		if (fromProp) return fromProp;
		return isLimitedVisualization
			? available.find((option) => option.value === MapType.Distribution) || available[0]
			: available[0];
	});
	const [activeEvent, setActiveEvent] = useState<GPEventResponse | null>(null);
	const prevMapTypeRef = useRef<MapType | undefined>(mapType);

	const deferredEvents = useDeferredValue(events);
	const deferredFilters = useDeferredValue(filters);

	const translatedVisualMapOption = useMemo(
		() =>
			VISUAL_MAP_OPTIONS.map((option) => ({
				...option,
				label: _any(option.label),
			})),
		[]
	);

	// Derived: available map types for this event type and visualization mode
	const visualMapOption = useMemo(() => {
		let options = translatedVisualMapOption.filter((option) => {
			if (option.condition) return option.condition(eventType as any);
			return true;
		});
		if (isLimitedVisualization) {
			options = options.filter((option) => ![MapType.PointMap, MapType.Directional, MapType.Zonal].includes(option.value as MapType));
		}
		return options;
	}, [eventType, isLimitedVisualization, translatedVisualMapOption]);

	const eventParameterOptions = useMemo(() => {
		return EVENT_PARAMETER_OPTIONS[eventType]?.map((config) => {
			if ([PASS_BODY_PART_FILTER_KEY, SHOT_BODY_PART_FILTER_KEY, SAVE_BODY_PART_FILTER_KEY].includes(config?.filter_key || "")) {
				return {
					...config,
					options: config.options.map((option) => ({
						...option,
						isDisabled: !config.filter_key ? false : eventFilter.eventsCount(config.filter_key, [option.value], []) === 0,
					})),
				};
			}
			if (eventType === EventDistributionEnum.Pass && selectedMapOption?.value === MapType.Directional && config.key === "success") {
				return {
					...config,
					options: config.options.filter((option) => option.value !== "false"),
				};
			}
			return config;
		});
	}, [eventType, selectedMapOption?.value, eventFilter]);

	// Derived: events after period/team/player filters
	const filteredEvents = useMemo(() => {
		if (!eventFilter) return [];
		return eventFilter.filterByCombinedConditions(deferredFilters, deferredEvents);
	}, [deferredFilters, deferredEvents, eventFilter]);

	// Derived: events after directional + parameter filters
	const eventsWithParameterFilters = useMemo(() => {
		if (!eventFilter) return [];
		let base = filteredEvents;
		if (selectedMapOption?.value === MapType.Directional) {
			base = filteredEvents.filter((event: GPEventResponse) => {
				if (!event?.x || !event?.y || !event.details?.next_event_coordinates) return false;
				if (eventType === EventDistributionEnum.Pass && event.success === false) return false;
				return true;
			});
		}
		const keys = Object.keys(parameterFilters);
		if (keys.length === 0) return base;
		const conditions = keys.map((key) => ({
			type: key,
			value: [parameterFilters[key].value],
			...(parameterFilters[key].flag ? { flag: parameterFilters[key].flag } : {}),
		}));
		return eventFilter.filterByConditions(conditions, base);
	}, [eventFilter, filteredEvents, parameterFilters, selectedMapOption?.value, eventType]);

	// Sync isOpen with data: open when we have events and no param filters
	useEffect(() => {
		if (Object.keys(parameterFilters).length === 0) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- sync open state from derived data
			setIsOpen(eventsWithParameterFilters.length > 0);
		}
	}, [eventsWithParameterFilters.length, parameterFilters]);

	// Sync selectedMapOption from mapType prop; correct when current selection is no longer in list.
	// Only sync from prop when prop CHANGED (external update). Do not overwrite when user just selected
	// and parent hasn't updated yet (selectedMapOption has new value, mapType prop still has old).
	useEffect(() => {
		if (visualMapOption.length === 0) return;
		const currentValid = visualMapOption.some((o) => o.value === selectedMapOption?.value);
		const preferred = visualMapOption.find((o) => o.value === mapType) ?? visualMapOption[0];

		if (!currentValid) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- sync selection from prop / correct when invalid
			setSelectedMapOption(preferred);
			if (preferred.value !== mapType) onMapTypeChange?.(identifier, preferred.value as MapType);
			prevMapTypeRef.current = mapType;
			return;
		}
		// Only sync from prop when prop changed externally (e.g. loaded saved state). Don't overwrite user selection.
		const propChanged = mapType !== prevMapTypeRef.current;
		if (propChanged && mapType && visualMapOption.some((o) => o.value === mapType)) {
			const fromProp = visualMapOption.find((o) => o.value === mapType)!;
			// eslint-disable-next-line react-hooks/set-state-in-effect -- sync selection from prop
			setSelectedMapOption(fromProp);
			onMapTypeChange?.(identifier, fromProp.value as MapType);
			prevMapTypeRef.current = mapType;
		} else if (mapType) {
			prevMapTypeRef.current = mapType;
		}
	}, [mapType, visualMapOption, identifier, onMapTypeChange, selectedMapOption?.value]);

	// Sync parameterFilters from parameters prop
	useEffect(() => {
		if (!parameters?.length) return;
		// eslint-disable-next-line react-hooks/set-state-in-effect -- sync local state from parameters prop
		setParameterFilters((prev) => {
			const updated = { ...prev };
			let hasChanges = false;
			EVENT_PARAMETER_OPTIONS[eventType]?.forEach((config) => {
				const param = parameters.find((p) => p.key === config.key);
				if (param) {
					const selectedOption = config.options.find((option) => option.value === param.value);
					if (selectedOption) {
						const newFilter = { value: selectedOption.value, label: _(selectedOption.label as any) };
						if (updated[config.key]?.value !== newFilter.value) {
							updated[config.key] = newFilter;
							hasChanges = true;
						}
					}
				}
			});
			return hasChanges ? updated : prev;
		});
	}, [parameters, eventType]);

	// Clear unsuccessful param filter when Pass + Directional (invalid combo)
	useEffect(() => {
		if (
			eventType === EventDistributionEnum.Pass &&
			selectedMapOption?.value === MapType.Directional &&
			parameterFilters.success?.value === "false"
		) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- one-time cleanup for invalid filter combo
			setParameterFilters((prev) => {
				const next = { ...prev };
				delete next.success;
				return next;
			});
		}
	}, [eventType, selectedMapOption?.value, parameterFilters.success?.value]);

	const handlePointMapClick = (event: GPEventResponse) => {
		setActiveEvent(event);
	};

	return (
		<div className="widget-container">
			<div className="widget-container__header">
				<div
					className={clsx("widget-container__header-title", {
						"is-open": isOpen,
					})}
				>
					<div className="widget-container__header-title__left">
						<div
							className={clsx("collapse-indicator", {
								"is-open": isOpen,
							})}
							onClick={() => setIsOpen((prev) => !prev)}
						>
							<span className="icon icon-chevron-down-grey icon-24 collapse-icon"></span>
						</div>
						<h2>
							{_any(`gamePage.dataInsights.eventType.${eventType}`)} ({eventsWithParameterFilters?.length ?? 0})
						</h2>
					</div>
					<div className="widget-container__header-title__actions">
						<div
							className={clsx("widget-container__header-title__actions__tooltip-container", {
								"has-limited-visualization": isLimitedVisualization,
								"has-directional": selectedMapOption?.value === MapType.Directional,
							})}
						>
							{isLimitedVisualization && (
								<TooltipComponent
									title={_("gamePage.dataInsights.label.limitedExpTooltip")}
									children={
										<div className="widget-container__header-title__actions__tooltip">
											<p className="widget-container__header-title__actions__tooltip__text">
												<Icon icon="info-outline" className="icon-info-outline" />
												<span>{_("gamePage.dataInsights.label.limitedExperience")}</span>
											</p>
										</div>
									}
								/>
							)}
							{selectedMapOption?.value === MapType.Directional && (
								<TooltipComponent
									title={_("gamePage.dataInsights.label.limitedDirectionalDescription")}
									children={
										<div className="widget-container__header-title__actions__tooltip">
											<p className="widget-container__header-title__actions__tooltip__text">
												<Icon icon="info-outline" className="icon-info-outline" />
												<span>{_("gamePage.dataInsights.label.limitedDirectionalTooltip")}</span>
											</p>
										</div>
									}
								/>
							)}
						</div>
						<SelectComponent
							value={selectedMapOption}
							items={visualMapOption}
							isCleareable={false}
							menuPortalTarget={true}
							handleChange={(value) => {
								setSelectedMapOption(value as { value: string; label: string });
								onMapTypeChange?.(identifier, value?.value as MapType);
							}}
						/>

						{customWidget && (
							<Button
								className="btn btn--grey"
								onPress={() => {
									onDeleteCustom?.(identifier);
								}}
							>
								<span className="icon-trash-new icon-new icon-grey icon-16" />
							</Button>
						)}
					</div>
				</div>
			</div>

			<div
				className={clsx("widget-container__body", "collapsible-content", {
					"is-open": isOpen,
				})}
			>
				{isOpen && (
					<>
						<div className="widget-container__body-actions">
							<div className="widget-container__body-actions__selects">
								{eventParameterOptions?.map((config) => {
									const optionsLength = config.options?.length;
									const optionsDisabled = config.options?.filter((option) => option.isDisabled).length;

									const currentFilter = parameterFilters[config.key];
									const availableOptions =
										config.options?.map((option) => ({
											...option,
											label: _(option.label as any),
											isDisabled: option.isDisabled,
										})) || [];
									const isValidValue = currentFilter && availableOptions.some((option) => option.value === currentFilter.value);
									const selectValue = isValidValue
										? currentFilter
										: {
												value: "",
												label: _(config.label as any),
											};

									return (
										<SelectComponent
											key={config.key}
											value={selectValue}
											items={availableOptions}
											isCleareable={true}
											menuPortalTarget={true}
											placeholder={_(config.placeholder as any)}
											handleChange={(value) => {
												setParameterFilters((prev) => {
													const updated = { ...prev };
													if (value == null) {
														delete updated[config.key]; // Remove key if value is null
													} else {
														updated[config.key] = value as SelectOption;
													}
													return updated;
												});
											}}
											isDisabled={optionsLength && optionsLength === optionsDisabled ? true : false}
										/>
									);
								})}
							</div>

							{[MapType.PointMap, MapType.Directional, MapType].includes(mapType as MapType) &&
								EVENT_COLOR_DEFINITION[eventType as keyof typeof EVENT_COLOR_DEFINITION] && (
									<div className="widget-container__header-actions__colors">
										<div className="color-grid">
											{EVENT_COLOR_DEFINITION[eventType as keyof typeof EVENT_COLOR_DEFINITION]?.map((color) => (
												<div key={color.key} className="color-item">
													<div className="color-circle" style={{ backgroundColor: color.color || "#ccc" }}></div>
													<div className="color-definition">{_(color.label as any)}</div>
												</div>
											))}
										</div>
									</div>
								)}
						</div>
						<div className="widget-align-items-flex-start">
							<div className="visual-container">
								<Suspense
									fallback={
										<div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
											<Loader2 className="animate-spin" size={24} />
										</div>
									}
								>
									{(selectedMapOption?.value === MapType.PointMap || selectedMapOption?.value === MapType.Directional) && (
										<PointMapWithZoomSwitch
											filters={filters}
											events={eventsWithParameterFilters || []}
											activeEvent={activeEvent}
											clickEvent={handlePointMapClick}
											directional={selectedMapOption?.value === MapType.Directional}
											showPlayerNumber={showPlayerNumber}
											category={category}
											eventType={eventType}
										/>
									)}
									{selectedMapOption?.value === MapType.Zonal && (
										<ZonalMap events={eventsWithParameterFilters || []} category={category} />
									)}
									{selectedMapOption?.value === MapType.Distribution && (
										<DistributionChart events={eventsWithParameterFilters || []} />
									)}
									{selectedMapOption?.value === MapType.CulmulativeChart && (
										<CumulativeProgressionCharts events={eventsWithParameterFilters || []} eventType={eventType} />
									)}
								</Suspense>
							</div>
							<div className="events-container">
								<Events
									events={eventsWithParameterFilters || []}
									activeEvent={activeEvent}
									players={players}
									filters={filters}
									eventType={eventType}
									parameterFilters={parameterFilters}
									onRowClick={handlePointMapClick}
								/>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

// Custom comparison function for memo to prevent unnecessary re-renders
const areEqual = (prevProps: WidgetProps, nextProps: WidgetProps) => {
	// Compare primitive props
	if (
		prevProps.eventType !== nextProps.eventType ||
		prevProps.showPlayerNumber !== nextProps.showPlayerNumber ||
		prevProps.mapType !== nextProps.mapType ||
		prevProps.customWidget !== nextProps.customWidget ||
		prevProps.isLimitedVisualization !== nextProps.isLimitedVisualization ||
		prevProps.identifier !== nextProps.identifier ||
		prevProps.category !== nextProps.category
	) {
		return false;
	}

	// Compare events by length (simple check is usually sufficient and we check other things like players etc.)
	if (prevProps.events?.length !== nextProps.events?.length) {
		return false;
	}

	if (prevProps.filters?.length !== nextProps.filters?.length) {
		return false;
	}

	if (prevProps.parameters?.length !== nextProps.parameters?.length) {
		return false;
	}

	// Deep compare filters if length matches
	if (prevProps.filters && nextProps.filters && JSON.stringify(prevProps.filters) !== JSON.stringify(nextProps.filters)) {
		return false;
	}

	// Compare players object keys
	if (Object.keys(prevProps.players || {}).join(",") !== Object.keys(nextProps.players || {}).join(",")) {
		return false;
	}

	return true;
};

export default memo(Widget, areEqual);
